/**
 * Google Login Button Component
 * 
 * A styled button that initiates Google OAuth authentication.
 * Displays a Google icon and localized text.
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { authService } from '../../services/auth.service';

interface GoogleLoginButtonProps {
  /** Optional className for additional styling */
  className?: string;
  /** Whether to show "Sign up" instead of "Sign in" */
  isSignUp?: boolean;
  /** Optional return URL after authentication */
  returnUrl?: string;
}

const GoogleLoginButton = ({ className = '', isSignUp = false, returnUrl }: GoogleLoginButtonProps) => {
  const { t, i18n } = useTranslation();
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if Google auth is enabled
    authService.getGoogleAuthStatus()
      .then(status => setIsEnabled(status.enabled))
      .catch(() => setIsEnabled(false));
  }, []);

  const handleGoogleLogin = () => {
    setIsLoading(true);
    
    // Build the OAuth URL with language and return URL
    const params = new URLSearchParams();
    params.set('language', i18n.language as 'fr' | 'en');
    if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }

    // Redirect to backend OAuth endpoint
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiUrl}/api/auth/google?${params.toString()}`;
  };

  // Don't render if Google auth is not enabled
  if (isEnabled === false) {
    return null;
  }

  // Show loading skeleton while checking
  if (isEnabled === null) {
    return (
      <div className={`h-11 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse ${className}`} />
    );
  }

  return (
    <button
      type="button"
      onClick={handleGoogleLogin}
      disabled={isLoading}
      className={`
        w-full flex items-center justify-center gap-3 px-4 py-2.5
        bg-white dark:bg-slate-800 
        border border-slate-300 dark:border-slate-600
        rounded-lg shadow-sm
        text-slate-700 dark:text-slate-200 font-medium
        hover:bg-slate-50 dark:hover:bg-slate-700
        focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:ring-offset-2 dark:focus:ring-offset-slate-900
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${className}
      `}
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-slate-300 dark:border-slate-600 border-t-violet-500 rounded-full animate-spin" />
      ) : (
        <>
          {/* Google Icon */}
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>
            {isSignUp 
              ? t('auth.google.signUp', 'S\'inscrire avec Google')
              : t('auth.google.signIn', 'Se connecter avec Google')
            }
          </span>
        </>
      )}
    </button>
  );
};

export default GoogleLoginButton;
